import { authedGet, authedWrite } from './apiClient';
import { API_BASE } from './config';
import { authToken } from './auth';
import { withViewAs } from './viewAs';
import { persistResource } from './store';

export const COMPANY_VENDORS_KEY = 'company-vendors';
export const vendorProfileKey = (id: number): string => `company-vendor:${id}`;

export type VendorScope = 'COMPANY' | 'LOCATION';

export interface CompanyVendor {
  id: number;
  scope: VendorScope;
  locationCode: string | null;
  name: string;
  // N-097 (owner, 2026-09-10): free text, e.g. Computers, Cleaning, Phones,
  // Alarm, Linen/Sheets, Pest Control — whatever the vendor sheets use.
  category: string | null;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  // N-096: always null for a COMPANY-scope vendor — its notes moved to
  // clinicNotes, one per clinic. Still the flat per-vendor note for a
  // LOCATION-scope vendor, unchanged from before.
  notes: string | null;
  // COMPANY-scope only: { locationCode: notes }, limited to the clinics the
  // caller can see — a manager's copy never has another clinic's key in it.
  clinicNotes?: Record<string, string | null>;
  fileCount?: number;
}

export interface CompanyVendorFile {
  id: number;
  vendorId: number;
  url: string;
  name: string | null;
  addedBy: string;
  addedAt: string;
}

export async function fetchCompanyVendors(): Promise<{ vendors: CompanyVendor[] }> {
  const data = await authedGet<{ vendors: CompanyVendor[] }>('/api/v1/company/vendors');
  await persistResource(COMPANY_VENDORS_KEY, data);
  return data;
}

export async function fetchCompanyVendor(id: number): Promise<{ vendor: CompanyVendor; files: CompanyVendorFile[] }> {
  const data = await authedGet<{ vendor: CompanyVendor; files: CompanyVendorFile[] }>(
    `/api/v1/company/vendors/${id}`,
  );
  await persistResource(vendorProfileKey(id), data);
  return data;
}

export async function saveCompanyVendor(body: Record<string, unknown>, id?: number): Promise<void> {
  if (id) await authedWrite('PATCH', `/api/v1/company/vendors/${id}`, body);
  else await authedWrite('POST', '/api/v1/company/vendors', body);
}

export async function removeCompanyVendor(id: number): Promise<void> {
  await authedWrite('DELETE', `/api/v1/company/vendors/${id}`, {});
}

/** N-096: turn a clinic's own vendor into a company-wide one, in place. */
export async function promoteCompanyVendor(id: number): Promise<void> {
  await authedWrite('POST', `/api/v1/company/vendors/${id}/promote`, {});
}

/** N-096: the reverse — a company-wide vendor becomes one clinic's own. */
export async function demoteCompanyVendor(id: number, locationCode: string): Promise<void> {
  await authedWrite('POST', `/api/v1/company/vendors/${id}/demote`, { locationCode });
}

/** N-096: one clinic's own note on a company-wide vendor. */
export async function saveVendorClinicNote(id: number, locationCode: string, notes: string): Promise<void> {
  await authedWrite('PUT', `/api/v1/company/vendors/${id}/clinic-note`, { locationCode, notes });
}

export async function addVendorLink(vendorId: number, url: string, name: string): Promise<void> {
  await authedWrite('POST', `/api/v1/company/vendors/${vendorId}/attachments`, { url, name });
}

export async function removeVendorFile(fileId: number): Promise<void> {
  await authedWrite('DELETE', `/api/v1/company/vendor-files/${fileId}`, {});
}

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export async function uploadVendorFile(vendorId: number, file: File): Promise<void> {
  if (file.size === 0) throw new Error(`${file.name} is empty`);
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(
      `${file.name} is ${Math.round(file.size / 1048576)} MB — the limit is `
      + `${MAX_UPLOAD_BYTES / 1048576} MB. Put it in Drive and paste the link instead.`,
    );
  }
  const token = authToken.value;
  const res = await fetch(`${API_BASE}${withViewAs(`/api/v1/company/vendors/${vendorId}/files`)}`, {
    method: 'POST',
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      'content-type': file.type || 'application/octet-stream',
      'x-file-name': encodeURIComponent(file.name),
      'idempotency-key': crypto.randomUUID(),
    },
    body: file,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((body as { error?: string }).error || `upload failed (HTTP ${res.status})`);
  }
}

/**
 * N-097: rows sorted by category then name, blank category last — within
 * whatever list is passed in (a single table's rows, already filtered to
 * one scope/clinic). A pure sort, never mutates its input.
 */
export function sortVendorRows(rows: CompanyVendor[]): CompanyVendor[] {
  return [...rows].sort((a, b) => {
    const ac = a.category?.trim() || '';
    const bc = b.category?.trim() || '';
    if (ac === '' && bc !== '') return 1;
    if (bc === '' && ac !== '') return -1;
    if (ac !== bc) return ac.localeCompare(bc);
    return a.name.localeCompare(b.name);
  });
}

/**
 * N-097: the distinct categories already in use across every vendor the
 * caller can see, for the add/edit form's datalist — exact strings only
 * (no fuzzy grouping of near-matches), sorted for a stable list.
 */
export function categoriesInUse(vendors: CompanyVendor[]): string[] {
  const set = new Set<string>();
  for (const v of vendors) {
    const c = v.category?.trim();
    if (c) set.add(c);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}
